// Admin API 集中调用
import { api, apiUpload, tokenStore } from './client.js'
import type { Model, Order, PaymentQrCode, AuditLog, AdminTaskItem, User } from '@vivilo/shared'

// ── auth ───────────────────────────────────────────────────────────
interface MeResponse {
  user: User & { is_admin: boolean }
}
export async function login(account: string, password: string) {
  const r = await api<{ access_token: string; refresh_token: string; user: User & { is_admin: boolean } }>(
    '/v1/auth/login',
    { body: { account, password } },
  )
  tokenStore.set(r.access_token, r.refresh_token)
  return r
}
export async function logout() {
  const r = tokenStore.getRefresh()
  await api('/v1/auth/logout', { body: { refresh_token: r } }).catch(() => undefined)
  tokenStore.clear()
}
export async function fetchMe() {
  return api<MeResponse>('/v1/auth/me')
}

// ── 大盘 ────────────────────────────────────────────────────────────
export async function getStatsOverview() {
  return api<{
    total_users: number
    active_today: number
    tasks_today: number
    orders_today: number
    revenue_today_cny: number
    revenue_total_cny: number
    paying_users: number
    tokens_today: { input: number; output: number }
    tokens_total: { input: number; output: number }
    credits_charged_today: number
    credits_charged_total: number
    llm_cost_today_cny: number
    llm_cost_total_cny: number
  }>('/v1/admin/stats/overview')
}
export async function getPaymentChannelsStats() {
  return api<{ items: Array<Pick<PaymentQrCode, 'channel' | 'display_name' | 'daily_total_cny' | 'daily_limit_cny' | 'monthly_total_cny' | 'monthly_limit_cny' | 'status'>> }>(
    '/v1/admin/stats/payment-channels',
  )
}
export async function getAutoMatchRate() {
  return api<{ total: number; auto: number; rate: number }>('/v1/admin/stats/auto-match-rate')
}

// ── users ──────────────────────────────────────────────────────────
export interface AdminUserRow {
  id: string
  phone: string | null
  email: string
  display_name: string | null
  status: 'active' | 'frozen' | 'deleted'
  is_admin: boolean
  created_at: string
  last_login_at: string | null
  credit_balance: number | null
  credit_frozen: number | null
  member_tier: 'trial' | 'basic' | 'pro' | null
  member_expires_at: string | null
}
export async function listUsers(q?: { q?: string; status?: 'active' | 'frozen' | 'deleted' }) {
  const p = new URLSearchParams()
  if (q?.q) p.set('q', q.q)
  if (q?.status) p.set('status', q.status)
  return api<{ items: AdminUserRow[] }>(`/v1/admin/users?${p}`)
}
export async function setUserStatus(id: string, status: 'active' | 'frozen' | 'deleted') {
  return api<{ ok: true }>(`/v1/admin/users/${id}/status`, { method: 'PUT', body: { status } })
}
export async function grantUserCredits(id: string, delta: number, note?: string) {
  return api<{ ok: true }>(`/v1/admin/users/${id}/grant-credits`, { body: { delta, note } })
}
export async function grantUserMembership(id: string, tier: 'basic' | 'pro', months: number, withCredits: boolean) {
  return api<{ ok: true; expires_at: string | null }>(`/v1/admin/users/${id}/grant-membership`, {
    body: { tier, months, with_credits: withCredits },
  })
}
export async function getUserDetail(id: string) {
  return api<{
    user: User
    membership: { tier: string; expires_at: string } | null
    credits: { balance: number; frozen: number } | null
  }>(`/v1/admin/users/${id}`)
}

// ── 意见反馈 + 客服配置 ─────────────────────────────────────────
export interface AdminFeedback {
  id: string
  content: string
  image_url: string | null
  status: 'open' | 'resolved'
  admin_note: string | null
  created_at: string
  resolved_at: string | null
  user_phone: string | null
  user_email: string | null
  user_name: string | null
}
export async function listFeedback() {
  return api<{ items: AdminFeedback[] }>(`/v1/admin/feedback`)
}
export async function updateFeedback(id: string, input: { status?: 'open' | 'resolved'; admin_note?: string }) {
  return api<{ ok: true }>(`/v1/admin/feedback/${id}`, { method: 'PUT', body: input })
}
export async function getSupportConfig() {
  return api<{ wechat_qr_url: string | null; source: 'upload' | 'url' | null }>(`/v1/support/config`)
}
export async function setSupportConfig(wechat_qr_url: string) {
  return api<{ ok: true }>(`/v1/admin/support/config`, { method: 'PUT', body: { wechat_qr_url } })
}
/** 客服二维码直接上传(multipart) */
export async function uploadSupportQr(file: File) {
  const form = new FormData()
  form.append('image', file)
  return apiUpload<{ ok: true; url: string }>(`/v1/admin/support/qr-image`, form)
}

// ── billing:充值套餐(后台配置) ─────────────────────────────────
export interface AdminCreditPack {
  id: string
  credits: number
  bonus: number
  price_cny: number
  label: string | null
  sort: number
  enabled: boolean
}
export async function listCreditPacksAdmin() {
  return api<{ items: AdminCreditPack[] }>(`/v1/admin/billing/credit-packs`)
}
export async function createCreditPack(input: Omit<AdminCreditPack, 'id'>) {
  return api<{ id: string }>(`/v1/admin/billing/credit-packs`, { body: input })
}
export async function updateCreditPack(id: string, input: Partial<Omit<AdminCreditPack, 'id'>>) {
  return api<{ ok: true }>(`/v1/admin/billing/credit-packs/${id}`, { method: 'PUT', body: input })
}
export async function deleteCreditPack(id: string) {
  return api<{ ok: true }>(`/v1/admin/billing/credit-packs/${id}`, { method: 'DELETE' })
}

// ── 订阅档位配置 ────────────────────────────────────────────────────
export interface MembershipPlan {
  monthly_fee_cny: number // 分
  granted_credits: number
}
export type MembershipPlans = Record<'basic' | 'pro', MembershipPlan>
export async function getMembershipPlansAdmin() {
  return api<{ plans: MembershipPlans }>(`/v1/admin/billing/membership-plans`)
}
export async function updateMembershipPlans(plans: MembershipPlans) {
  return api<{ ok: true }>(`/v1/admin/billing/membership-plans`, { method: 'PUT', body: plans })
}

// ── 注册赠送积分(系统配置页,与订阅档位无关) ──
// 功能灰度可见性:空=全隐藏 / "0"=全部可见 / 邮箱或 user_id 逗号分隔=白名单
export async function getFeatureVisibility() {
  return api<{ youtube_analysis: string }>(`/v1/admin/billing/feature-visibility`)
}
export async function getYtScanConfig() {
  return api<{ discover_interval: '4h' | '24h' }>(`/v1/admin/billing/yt-scan-config`)
}
export async function setYtScanConfig(discover_interval: '4h' | '24h') {
  return api<{ ok: true }>(`/v1/admin/billing/yt-scan-config`, {
    method: 'PUT',
    body: { discover_interval },
  })
}
export async function setFeatureVisibility(youtube_analysis: string) {
  return api<{ ok: true }>(`/v1/admin/billing/feature-visibility`, {
    method: 'PUT',
    body: { youtube_analysis },
  })
}

export async function getSignupBonus() {
  return api<{ credits: number }>(`/v1/admin/billing/signup-bonus`)
}
export async function setSignupBonus(credits: number) {
  return api<{ ok: true }>(`/v1/admin/billing/signup-bonus`, { method: 'PUT', body: { credits } })
}

// ── 差价系数(扣费 = 模型成本 × 系数 × 100积分/元)──
export async function getPlatformMargin() {
  return api<{ margin: number; env_default: number }>(`/v1/admin/billing/margin`)
}
export async function updatePlatformMargin(margin: number) {
  return api<{ ok: true; margin: number }>(`/v1/admin/billing/margin`, { method: 'PUT', body: { margin } })
}

// ── orders ─────────────────────────────────────────────────────────
export async function listAdminOrders(status?: string) {
  return api<{ items: Order[] }>(`/v1/admin/payments/orders${status ? '?status=' + status : ''}`)
}
export async function manualConfirmPayment(input: {
  order_id: string
  paid_amount_cny: number
  paid_at: string
  qr_code_id?: string
  payer_info?: string
  note?: string
}) {
  return api<{ ok: true; callback_id: string }>(`/v1/admin/payments/manual-confirm`, { body: input })
}
export async function importIncomingPayments(input: {
  channel: 'wechat' | 'alipay'
  qr_code_id?: string
  raw_text: string
}) {
  return api<{ total_lines: number; parsed: number; results: Array<Record<string, unknown>> }>(
    '/v1/admin/payments/import-incoming',
    { body: input },
  )
}

// ── orphans ────────────────────────────────────────────────────────
export async function listOrphans() {
  return api<{ items: Array<Record<string, unknown>> }>('/v1/admin/payments/orphans')
}
export async function resolveOrphan(id: string, action: 'link_to_order' | 'refund' | 'hold', resolved_order_id?: string, admin_note?: string) {
  return api<{ ok: true }>(`/v1/admin/payments/orphans/${id}/resolve`, {
    body: { action, resolved_order_id, admin_note },
  })
}

// ── 收款码池 ───────────────────────────────────────────────────────
export async function listQrCodes() {
  return api<{ items: PaymentQrCode[] }>('/v1/admin/payments/qr-codes')
}
export async function upsertQrCode(input: Partial<PaymentQrCode> & { qr_image_url?: string }) {
  if ('id' in input && input.id) {
    return api<{ ok: true }>(`/v1/admin/payments/qr-codes/${input.id}`, { method: 'PUT', body: input })
  }
  return api<{ id: string }>('/v1/admin/payments/qr-codes', { body: input })
}
export async function deleteQrCode(id: string) {
  return api<{ ok: true }>(`/v1/admin/payments/qr-codes/${id}`, { method: 'DELETE' })
}

// ── 模型管理 ───────────────────────────────────────────────────────
export async function listAdminModels() {
  return api<{ items: Model[]; platform_margin: number }>('/v1/admin/models')
}
export async function updateModel(id: string, input: Partial<Model>) {
  return api<{ ok: true }>(`/v1/admin/models/${id}`, { method: 'PUT', body: input })
}

// ── 任务 ───────────────────────────────────────────────────────────
export async function listAdminTasks(status?: string) {
  return api<{ items: AdminTaskItem[] }>(`/v1/admin/tasks${status ? '?status=' + status : ''}`)
}

// ── 审计 ───────────────────────────────────────────────────────────
export async function listAdminAudit(params?: { action?: string; actor_id?: string; since?: string }) {
  const p = new URLSearchParams()
  if (params?.action) p.set('action', params.action)
  if (params?.actor_id) p.set('actor_id', params.actor_id)
  if (params?.since) p.set('since', params.since)
  return api<{ items: AuditLog[] }>(`/v1/admin/audit?${p}`)
}

// ── 预设 ───────────────────────────────────────────────────────────
export async function listAdminPresets(category?: string) {
  return api<{ items: Array<Record<string, unknown>> }>(`/v1/admin/presets${category ? '?category=' + category : ''}`)
}
export async function upsertPreset(input: Record<string, unknown>) {
  if (input.id) {
    return api<{ ok: true }>(`/v1/admin/presets/${input.id}`, { method: 'PUT', body: input })
  }
  return api<{ id: string }>('/v1/admin/presets', { body: input })
}

// ── 投放回流(shortdramahd 素材表现) ────────────────────────────────
export interface CreativePerfRow {
  id: string
  filename: string
  matched_cut_id: string | null
  project_id: string | null
  drama_key: string | null
  drama_title: string | null
  spend_usd: string | null
  hook_rate_pct: string | null
  thruplay_rate_pct: string | null
  roi: string | null
  drama_rank: { hook?: number; roi?: number; total?: number } | null
  low_confidence: boolean
  remote_uploaded_at: string | null
  synced_at: string
}
export async function getCreativePerf(limit = 200) {
  return api<{ items: CreativePerfRow[]; total: number; matched: number; spend_usd: string | null }>(
    `/v1/admin/creative-perf/?limit=${limit}`,
  )
}
export interface CreativePerfDiag {
  secret_configured: boolean
  feed_base_url: string
  last_sync: string | null
  rows_received: number
  rows_matched: number
  latest_synced_at: string | null
  total_spend_usd: string | null
  cuts_with_filename: number
  feed_rows_with_sl_code: number
  unmatched_samples: Array<string | null>
  our_recent_filenames: Array<string | null>
  remote_row_sample: Record<string, unknown> | null
  note: string
}
export interface FunnelStats {
  n: number
  spend: number | null
  hook: number | null
  thruplay: number | null
  avg_watch: number | null
  ctr: number | null
  link_ctr: number | null
  cpa: number | null
  roi: number | null
}
export interface CreativePerfFunnel {
  min_impressions: number
  ours: FunnelStats | null
  baseline: FunnelStats | null
  by_drama: Array<FunnelStats & { project_id: string | null; name: string | null }>
  ours_below_threshold: number
}
export async function getCreativePerfFunnel() {
  return api<CreativePerfFunnel>('/v1/admin/creative-perf/funnel')
}
export async function getCreativePerfDiag() {
  return api<CreativePerfDiag>('/v1/_diag/creative-perf')
}
export async function triggerCreativePerfSync() {
  return api<{ ok: true; message: string }>('/v1/admin/creative-perf/sync', { body: {} })
}

// ── 选题分析(平台开关 + TikHub 限额) ──────────────────────────────
export interface TopicConfig {
  enabled: string[]
  daily_limit: number
  tikhub_key_set: boolean
  usage: { requests: number; failed: number; cost_usd: number }
  platforms: Array<{ id: string; label: string; source: 'official' | 'tikhub'; verified: boolean }>
}
export async function getTopicConfig() {
  return api<TopicConfig>('/v1/admin/billing/topic-config')
}
export async function setTopicConfig(input: { enabled?: string[]; daily_limit?: number }) {
  return api<{ ok: true }>('/v1/admin/billing/topic-config', { method: 'PUT', body: input })
}

export interface TopicProbeResult {
  ok: boolean
  api: { profile: string; posts: string; profileParam: string; postsParam: string } | null
  title?: string
  subs?: number
  raw_items: number
  parsed: number
  sample: { nativeId: string; title: string; views: number; durationSec: number } | null
  error?: string
}
export async function calibrateTopicEndpoints(platform: string) {
  return api<{
    chosen: { profile: string; posts: string; profileParam: string; postsParam: string }
    candidates: {
      profile: Array<{ path: string; summary: string; params: string[] }>
      posts: Array<{ path: string; summary: string; params: string[] }>
    }
  }>(`/v1/admin/billing/topic-endpoints/${platform}/calibrate`, { body: {} })
}
export async function setTopicEndpoints(
  platform: string,
  input: { profile: string; posts: string; profileParam: string; postsParam: string },
) {
  return api<{ ok: true }>(`/v1/admin/billing/topic-endpoints/${platform}`, { method: 'PUT', body: input })
}
export async function probeTopicPlatform(platform: string, handle: string) {
  return api<TopicProbeResult>('/v1/admin/billing/topic-probe', { body: { platform, handle } })
}

// ── Gemini File API 存量(每项目 20GB 硬上限,与计费无关) ──
export interface GeminiFileUsage {
  configured: boolean
  count?: number
  used_gb?: number
  limit_gb?: number
  used_pct?: number
  by_state?: Record<string, number>
  by_age?: Record<string, { count: number; gb: number }>
  purgeable?: { count: number; gb: number }
  safe_purge_hours?: number
  oldest?: string | null
  newest?: string | null
}
export async function getGeminiFileUsage() {
  return api<GeminiFileUsage>(`/v1/admin/storage/gemini-files`)
}
export async function purgeGeminiFiles() {
  return api<{
    scanned: number
    matched: number
    deleted: number
    freed_gb: number
    older_than_hours: number
    remaining: number
    failures: string[]
  }>(`/v1/admin/storage/gemini-files/purge`, { method: 'POST', body: {} })
}
